import { useEffect, useState } from "react";
import { CheckCircle2, KeyRound, User2, ShieldCheck, MapPin } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Profile = () => {
  const { user, isAdmin } = useAuth();
  const [locationName, setLocationName] = useState("");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user?.location_id) {
      setLocationName("");
      return;
    }
    api
      .get("/locations", {
        params: { location_id: user.location_id },
        _skipScope: true,
      })
      .then((r) => {
        // Admins can list locations; other roles get 403 → fall back to blank.
        if (Array.isArray(r.data) && r.data[0]) setLocationName(r.data[0].name);
      })
      .catch(() => setLocationName(""));
  }, [user?.location_id]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    if (!current || !next || !confirm) {
      setError("All fields are required.");
      return;
    }
    if (next.length < 6) {
      setError("New Password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      setError("New Password and Confirm New Password do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(
        "/auth/change-password",
        { current_password: current, new_password: next },
        { _skipScope: true }
      );
      setSuccess(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-vision-ice" data-testid="profile-page">
      <AppHeader />
      <main className="mx-auto max-w-[720px] px-6 py-10">
        <div className="mb-6">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-vision-slate/60">
            Account
          </div>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-vision-ink sm:text-4xl">
            My Profile
          </h1>
          <p className="mt-1 text-sm text-vision-slate/70">
            Your account details and password. Changes here only affect your
            own login.
          </p>
        </div>

        {/* Read-only identity card */}
        <section
          className="rounded-[20px] border bg-white p-6 shadow-card"
          style={{ borderColor: "rgba(52,73,94,0.08)" }}
          data-testid="profile-identity-card"
        >
          <IdentityRow
            icon={User2}
            label="Username"
            value={user?.username || "—"}
            testId="profile-username"
          />
          <IdentityRow
            icon={ShieldCheck}
            label="Role"
            value={user?.role || "—"}
            testId="profile-role"
          />
          <IdentityRow
            icon={MapPin}
            label="Location"
            value={
              isAdmin && !user?.location_id
                ? "All Locations"
                : locationName || "—"
            }
            testId="profile-location"
            last
          />
        </section>

        {/* Change password */}
        <section
          className="mt-6 rounded-[20px] border bg-white p-6 shadow-card"
          style={{ borderColor: "rgba(52,73,94,0.08)" }}
          data-testid="profile-change-password-card"
        >
          <header className="mb-5 flex items-center gap-3">
            <span
              className="flex h-9 w-9 items-center justify-center rounded-xl"
              style={{
                backgroundColor: "rgba(247, 144, 9, 0.14)",
                color: "var(--vision-orange)",
              }}
            >
              <KeyRound className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-display text-lg font-bold text-vision-ink">
                Change Password
              </h2>
              <p className="text-xs text-vision-slate/70">
                Verify your current password to set a new one. Minimum 6 characters.
              </p>
            </div>
          </header>

          <form onSubmit={submit} className="grid gap-4" data-testid="profile-change-password-form">
            <PwField
              label="Current Password"
              value={current}
              onChange={setCurrent}
              testId="profile-current-password"
              autoComplete="current-password"
            />
            <PwField
              label="New Password"
              value={next}
              onChange={setNext}
              testId="profile-new-password"
              autoComplete="new-password"
            />
            <PwField
              label="Confirm New Password"
              value={confirm}
              onChange={setConfirm}
              testId="profile-confirm-password"
              autoComplete="new-password"
            />

            {error && (
              <div
                className="rounded-xl border px-4 py-3 text-sm"
                style={{
                  backgroundColor: "rgba(230, 74, 25, 0.08)",
                  borderColor: "rgba(230, 74, 25, 0.35)",
                  color: "#B23A1A",
                }}
                data-testid="profile-error"
              >
                {error}
              </div>
            )}
            {success && (
              <div
                className="flex items-center gap-2 rounded-xl border px-4 py-3 text-sm"
                style={{
                  backgroundColor: "rgba(39, 174, 96, 0.10)",
                  borderColor: "rgba(39, 174, 96, 0.35)",
                  color: "#2E7D5A",
                }}
                data-testid="profile-success"
              >
                <CheckCircle2 className="h-4 w-4" />
                Password updated. Use it the next time you sign in.
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="btn-pill bg-[color:var(--vision-orange)] px-5 py-2.5 text-sm font-semibold text-white shadow-card hover:brightness-105 hover:shadow-card-lg disabled:opacity-60"
                data-testid="profile-submit"
              >
                {submitting ? "Saving…" : "Update Password"}
              </button>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
};

const IdentityRow = ({ icon: Icon, label, value, testId, last = false }) => (
  <div
    className={`flex items-center justify-between gap-4 py-3 ${last ? "" : "border-b"}`}
    style={{ borderColor: "rgba(52,73,94,0.08)" }}
  >
    <div className="flex items-center gap-3">
      <span
        className="flex h-9 w-9 items-center justify-center rounded-xl"
        style={{ backgroundColor: "rgba(0, 53, 158, 0.10)", color: "var(--vision-navy)" }}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60">
        {label}
      </div>
    </div>
    <div className="font-semibold text-vision-ink" data-testid={testId}>
      {value}
    </div>
  </div>
);

const PwField = ({ label, value, onChange, testId, autoComplete }) => (
  <label className="block">
    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60">
      {label}
    </span>
    <input
      type="password"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete={autoComplete}
      data-testid={testId}
      className="mt-1 block w-full rounded-xl border bg-white px-4 py-2.5 text-sm text-vision-ink outline-none transition focus:border-[color:var(--vision-orange)]"
      style={{ borderColor: "rgba(52,73,94,0.14)" }}
    />
  </label>
);

export default Profile;
